import { LOCAL_STORAGE_KEY_JWT } from '@/api'
import { NECTAR_API_URL } from '@/env'
import { isAxiosError } from 'axios'
import { useEffect, useRef } from 'react'
import { sendTaskHeartbeat } from '../api'

const HEARTBEAT_INTERVAL_MS = 10_000

type UseTaskHeartbeatOptions = {
  projectId: string | undefined
  taskId: string | undefined
  enabled?: boolean
  /** Called once when the server reports the assignment is gone (404/409) — e.g. expired. */
  onClosed?: () => void
}

/**
 * While a task screen is open, sends a presence beat every 10s; the server
 * accumulates active time. 404/409 = assignment closed — stop beating.
 * Network errors are silently skipped (no retries — next tick covers it).
 */
export function useTaskHeartbeat({ projectId, taskId, enabled = true, onClosed }: UseTaskHeartbeatOptions) {
  const onClosedRef = useRef(onClosed)
  onClosedRef.current = onClosed
  useEffect(() => {
    if (!enabled || !projectId || !taskId) return
    // Per-effect-run guard: a stale in-flight beat from a previous run (old
    // taskId) resolving with 404/409 must not silence the current run.
    // farewellSent dedupes the goodbye beat (pagehide then unmount would send
    // two); a successful regular beat resets it, so a bfcache return re-arms it.
    const guard = { stopped: false, farewellSent: false }

    const beat = async () => {
      if (guard.stopped) return
      try {
        await sendTaskHeartbeat(projectId, taskId, document.visibilityState === 'visible')
        guard.farewellSent = false
      } catch (err) {
        if (isAxiosError(err) && (err.response?.status === 404 || err.response?.status === 409)) {
          guard.stopped = true
          onClosedRef.current?.()
        }
      }
    }

    // Farewell beat: keepalive survives tab close (axios can't; sendBeacon
    // can't set Authorization) — otherwise up to 10s of tail would be lost.
    const farewell = () => {
      if (guard.stopped || guard.farewellSent) return
      guard.farewellSent = true
      try {
        const raw = localStorage.getItem(LOCAL_STORAGE_KEY_JWT)
        if (!raw) return
        void fetch(`${NECTAR_API_URL}/v1/projects/${projectId}/tasks/${taskId}/heartbeat`, {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${JSON.parse(raw)}`
          },
          body: JSON.stringify({ visible: false })
        }).catch(() => undefined)
      } catch {
        // best-effort
      }
    }

    void beat()
    const interval = setInterval(() => void beat(), HEARTBEAT_INTERVAL_MS)
    // out-of-band beat on visibility change — precise active/background boundary
    const onVisibilityChange = () => void beat()
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pagehide', farewell)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('pagehide', farewell)
      farewell()
      guard.stopped = true
    }
  }, [enabled, projectId, taskId])
}
